import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { floorsAPI, zonesAPI, sedesAPI, spacesAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Layers, MapPin, Plus, Trash2, Loader2, Building2, ChevronDown, ChevronRight, Grid3X3 } from 'lucide-react';
import { Toaster, toast } from 'sonner';

const FloorsZonesPage = () => {
  const { user } = useAuth();
  const [sedes, setSedes] = useState([]);
  const [selectedSede, setSelectedSede] = useState('');
  const [selectedSedeInfo, setSelectedSedeInfo] = useState(null);
  const [floors, setFloors] = useState([]);
  const [zones, setZones] = useState([]);
  const [zoneSpacesCount, setZoneSpacesCount] = useState({});
  const [loading, setLoading] = useState(true);
  const [floorDialog, setFloorDialog] = useState(false);
  const [zoneDialog, setZoneDialog] = useState(false);
  const [floorForm, setFloorForm] = useState({ name: '', floor_number: 1 });
  const [zoneForm, setZoneForm] = useState({ name: '', floor_id: '' });
  const [saving, setSaving] = useState(false);
  const [expandedFloors, setExpandedFloors] = useState({});

  useEffect(() => {
    loadSedes();
  }, []);

  useEffect(() => {
    if (selectedSede) {
      loadFloors();
      // Find selected sede info
      const sedeInfo = sedes.find(s => s.id === selectedSede);
      setSelectedSedeInfo(sedeInfo);
    }
  }, [selectedSede, sedes]);

  const loadSedes = async () => {
    try {
      const response = await sedesAPI.getAll();
      setSedes(response.data);
      if (response.data.length > 0) {
        setSelectedSede(response.data[0].id);
      }
    } catch (error) {
      console.error('Error loading sedes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFloors = async () => {
    try {
      const response = await floorsAPI.getAll(selectedSede);
      setFloors(response.data);
      
      // Auto-expand all floors
      const expanded = {};
      response.data.forEach(f => { expanded[f.id] = true; });
      setExpandedFloors(expanded);
      
      // Load zones for all floors
      const allZones = [];
      const spacesCount = {};
      
      for (const floor of response.data) {
        const zonesRes = await zonesAPI.getAll(floor.id);
        const floorZones = zonesRes.data.map(z => ({ 
          ...z, 
          floor_name: floor.name,
          floor_number: floor.floor_number 
        }));
        allZones.push(...floorZones);
        
        // Load spaces count for each zone
        for (const zone of zonesRes.data) {
          try {
            const spacesRes = await spacesAPI.getAll(zone.id);
            spacesCount[zone.id] = spacesRes.data?.length || 0;
          } catch (e) {
            spacesCount[zone.id] = 0;
          }
        }
      }
      setZones(allZones);
      setZoneSpacesCount(spacesCount);
    } catch (error) {
      console.error('Error loading floors:', error);
    }
  };

  const handleCreateFloor = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await floorsAPI.create({ ...floorForm, sede_id: selectedSede });
      toast.success('Piso creado exitosamente');
      setFloorDialog(false);
      setFloorForm({ name: '', floor_number: 1 });
      loadFloors();
    } catch (error) {
      toast.error('Error al crear piso');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateZone = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await zonesAPI.create(zoneForm);
      toast.success('Zona creada exitosamente');
      setZoneDialog(false);
      setZoneForm({ name: '', floor_id: '' });
      loadFloors();
    } catch (error) {
      toast.error('Error al crear zona');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFloor = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('¿Eliminar este piso y todas sus zonas?')) return;
    try {
      await floorsAPI.delete(id);
      toast.success('Piso eliminado');
      loadFloors();
    } catch (error) {
      toast.error('Error al eliminar piso');
    }
  };

  const handleDeleteZone = async (id) => {
    if (!window.confirm('¿Eliminar esta zona y todos sus espacios?')) return;
    try {
      await zonesAPI.delete(id);
      toast.success('Zona eliminada');
      loadFloors();
    } catch (error) {
      toast.error('Error al eliminar zona');
    }
  };

  const toggleFloor = (id) => {
    setExpandedFloors(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Get zones for a specific floor
  const getZonesForFloor = (floorId) => {
    return zones.filter(z => z.floor_id === floorId);
  };

  return (
    <AdminLayout>
      <Toaster position="top-right" />
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Pisos y Zonas
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Gestiona la estructura física: Empresa → Sede → Pisos → Zonas
            </p>
          </div>
          <div className="flex gap-2">
            <Select value={selectedSede} onValueChange={setSelectedSede}>
              <SelectTrigger className="w-full sm:w-[220px] rounded-sm" data-testid="sede-selector">
                <SelectValue placeholder="Seleccionar sede" />
              </SelectTrigger>
              <SelectContent>
                {sedes.map((sede) => (
                  <SelectItem key={sede.id} value={sede.id}>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-green-600" />
                      {sede.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Sede Info Header */}
        {selectedSedeInfo && (
          <Card className="border border-slate-200 bg-gradient-to-r from-blue-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#002FA7] rounded-sm flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold">{selectedSedeInfo.name}</h2>
                  <div className="flex items-center gap-4 mt-1">
                    {selectedSedeInfo.city && (
                      <span className="text-sm text-slate-500">{selectedSedeInfo.city}</span>
                    )}
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-sm text-xs font-medium">
                      <Building2 className="w-3 h-3" />
                      {user?.role === 'admin' ? 'Mi Empresa' : 'Empresa asociada'}
                    </span>
                  </div>
                </div>
                <div className="hidden sm:flex items-center gap-6 text-center">
                  <div>
                    <p className="text-2xl font-bold text-[#002FA7]">{floors.length}</p>
                    <p className="text-xs text-slate-500">Pisos</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-orange-600">{zones.length}</p>
                    <p className="text-xs text-slate-500">Zonas</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button 
            onClick={() => setFloorDialog(true)} 
            className="bg-[#002FA7] hover:bg-[#001B66] rounded-sm" 
            disabled={!selectedSede}
            data-testid="add-floor-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Piso
          </Button>
          <Button 
            onClick={() => { 
              setZoneForm({ name: '', floor_id: floors[0]?.id || '' }); 
              setZoneDialog(true); 
            }} 
            variant="outline"
            className="rounded-sm"
            disabled={floors.length === 0}
            data-testid="add-zone-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Zona
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
          </div>
        ) : !selectedSede ? (
          <Card className="border border-slate-200">
            <CardContent className="flex flex-col items-center justify-center h-48 text-center">
              <MapPin className="w-12 h-12 text-slate-300 mb-4" />
              <p className="text-slate-500">Selecciona una sede para ver sus pisos y zonas</p>
            </CardContent>
          </Card>
        ) : floors.length === 0 ? (
          <Card className="border border-slate-200">
            <CardContent className="flex flex-col items-center justify-center h-48 text-center">
              <Layers className="w-12 h-12 text-slate-300 mb-4" />
              <p className="text-slate-500">No hay pisos registrados en esta sede</p>
              <Button 
                onClick={() => setFloorDialog(true)} 
                className="mt-4 bg-[#002FA7] hover:bg-[#001B66] rounded-sm"
              >
                Crear primer piso
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {floors.map((floor) => {
              const floorZones = getZonesForFloor(floor.id);
              return (
                <Card key={floor.id} className="border border-slate-200" data-testid={`floor-card-${floor.id}`}>
                  <Collapsible open={expandedFloors[floor.id]} onOpenChange={() => toggleFloor(floor.id)}>
                    <CardHeader className="pb-2">
                      <CollapsibleTrigger className="w-full">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {expandedFloors[floor.id] ? (
                              <ChevronDown className="w-5 h-5 text-slate-400" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-slate-400" />
                            )}
                            <div className="w-10 h-10 bg-purple-100 rounded-sm flex items-center justify-center">
                              <Layers className="w-5 h-5 text-purple-600" />
                            </div>
                            <div className="text-left">
                              <CardTitle className="text-lg">{floor.name}</CardTitle>
                              <p className="text-xs text-slate-500">Piso #{floor.floor_number} • {floorZones.length} zona(s)</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <span className="hidden sm:inline-flex items-center gap-1.5 px-2 py-1 bg-green-50 text-green-700 rounded-sm text-xs font-medium border border-green-200">
                              <MapPin className="w-3 h-3" />
                              {selectedSedeInfo?.name}
                            </span>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={(e) => handleDeleteFloor(floor.id, e)} 
                              className="text-slate-400 hover:text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CollapsibleTrigger>
                    </CardHeader>
                    
                    <CollapsibleContent>
                      <CardContent className="pt-0 pl-14">
                        {floorZones.length === 0 ? (
                          <div className="text-center py-4 text-slate-400 border border-dashed border-slate-200 rounded-sm">
                            <p className="text-sm">No hay zonas en este piso</p>
                            <Button 
                              variant="link" 
                              className="text-[#002FA7] p-0 h-auto mt-1"
                              onClick={() => { 
                                setZoneForm({ name: '', floor_id: floor.id }); 
                                setZoneDialog(true); 
                              }}
                            >
                              + Agregar zona
                            </Button>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {floorZones.map((zone) => (
                              <div 
                                key={zone.id} 
                                className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-sm"
                                data-testid={`zone-card-${zone.id}`}
                              >
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 bg-orange-100 rounded-sm flex items-center justify-center">
                                    <MapPin className="w-4 h-4 text-orange-600" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-sm">{zone.name}</p>
                                    <p className="text-xs text-slate-500 flex items-center gap-1">
                                      <Grid3X3 className="w-3 h-3" />
                                      {zoneSpacesCount[zone.id] || 0} espacios
                                    </p>
                                  </div>
                                </div>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => handleDeleteZone(zone.id)} 
                                  className="text-slate-400 hover:text-red-500 h-7 w-7 p-0"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </CollapsibleContent>
                  </Collapsible>
                </Card>
              );
            })}
          </div>
        )}

        {/* Floor Dialog */}
        <Dialog open={floorDialog} onOpenChange={setFloorDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Cabinet Grotesk' }}>Nuevo Piso</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateFloor} className="space-y-4">
              {/* Show sede info */}
              {selectedSedeInfo && (
                <div className="p-3 bg-slate-50 rounded-sm border border-slate-200">
                  <p className="text-xs text-slate-500 mb-1">Se creará en:</p>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-green-600" />
                    <span className="font-medium">{selectedSedeInfo.name}</span>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label>Nombre del piso *</Label>
                <Input
                  value={floorForm.name}
                  onChange={(e) => setFloorForm({ ...floorForm, name: e.target.value })}
                  required
                  className="rounded-sm"
                  placeholder="Ej: Piso 1, Planta Baja"
                  data-testid="floor-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Número de piso</Label>
                <Input
                  type="number"
                  value={floorForm.floor_number}
                  onChange={(e) => setFloorForm({ ...floorForm, floor_number: parseInt(e.target.value) || 1 })}
                  required
                  className="rounded-sm"
                  data-testid="floor-number-input"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setFloorDialog(false)} className="rounded-sm">Cancelar</Button>
                <Button type="submit" className="bg-[#002FA7] hover:bg-[#001B66] rounded-sm" disabled={saving} data-testid="save-floor-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Guardar
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Zone Dialog */}
        <Dialog open={zoneDialog} onOpenChange={setZoneDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Cabinet Grotesk' }}>Nueva Zona</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateZone} className="space-y-4">
              {/* Show sede info */}
              {selectedSedeInfo && (
                <div className="p-3 bg-slate-50 rounded-sm border border-slate-200">
                  <p className="text-xs text-slate-500 mb-1">Sede:</p>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-green-600" />
                    <span className="font-medium">{selectedSedeInfo.name}</span>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label>Piso *</Label>
                <Select value={zoneForm.floor_id} onValueChange={(value) => setZoneForm({ ...zoneForm, floor_id: value })}>
                  <SelectTrigger className="rounded-sm" data-testid="zone-floor-select">
                    <SelectValue placeholder="Seleccionar piso" />
                  </SelectTrigger>
                  <SelectContent>
                    {floors.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        <div className="flex items-center gap-2">
                          <Layers className="w-4 h-4 text-purple-600" />
                          {f.name} (Piso #{f.floor_number})
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Nombre de la zona *</Label>
                <Input
                  value={zoneForm.name}
                  onChange={(e) => setZoneForm({ ...zoneForm, name: e.target.value })}
                  required
                  className="rounded-sm"
                  placeholder="Ej: Zona A, Área de Desarrollo"
                  data-testid="zone-name-input"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setZoneDialog(false)} className="rounded-sm">Cancelar</Button>
                <Button type="submit" className="bg-[#002FA7] hover:bg-[#001B66] rounded-sm" disabled={saving || !zoneForm.floor_id} data-testid="save-zone-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Guardar
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default FloorsZonesPage;
